import { useDynamicLocale } from '@/hoslog-intl';
import { ChevronDown } from 'lucide-react';
import { forwardRef } from 'react';
import { Select as Root, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select';

interface Props {
  label?: string;
  options: { label: string; value: string; labelEng?: string }[];
  placeholder?: string;
  selected?: string;
  value?: string;
  onValueChange?: (val: string) => void;
  className?: string;
  hideDefaultOption?: boolean;
}

const DEFAULT_VALUE = 'default';

const Select = forwardRef<HTMLSelectElement, Props>((props, ref) => {
  const { options, placeholder, label, value, onValueChange, className, hideDefaultOption = false } = props;
  const { t } = useDynamicLocale();
  return (
    <div className="flex gap-2 justify-center items-center">
      <div className="whitespace-nowrap text-caption font-normal text-primary">{label}</div>

      <Root
        value={value || DEFAULT_VALUE}
        onValueChange={(val) => {
          onValueChange?.(val === DEFAULT_VALUE ? '' : val);
        }}
      >
        <SelectTrigger
          icon={<ChevronDown className="size-4" />}
          className={`
            border border-surface-hover px-4 py-2 w-[136px]
            focus:ring-0 focus:ring-offset-0 focus:outline-none
            text-caption
            ${className || ''}
          `}
        >
          <SelectValue placeholder={placeholder} ref={ref} />
        </SelectTrigger>

        <SelectContent>
          {!hideDefaultOption && <SelectItem value={DEFAULT_VALUE}>{t('common.form.placeholder.select')}</SelectItem>}

          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Root>
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
